// ─── Realtime Notifications Hook (Admin) ────────────────────────────────────
//
// G2.9: Admin-side subscriber for `notification:new`, mirroring
// apps/pos/src/hooks/use-realtime-notifications.ts.
//
// Unlike the POS hook (which maintains its own Zustand store), the admin
// app already uses @tanstack/react-query for notification data. On every
// live event we:
//   • Invalidate the notifications list + unread-count queries so the
//     visible NotificationCenter page refetches from REST.
//   • Raise a toast for high-priority types (LOW_STOCK, EXPENSE_APPROVAL,
//     SYNC_CONFLICT, CREDIT_LIMIT). Lower-priority events update the
//     counter silently.
//
// Connection lifecycle: shared socket with use-sync-conflicts-realtime.
// First call opens the connection; this hook's own unmount tears it down
// only if nobody else is using it (socket-client.ts tracks refcounts).
//
// Traceability: G2.9, FR-NOTIF-004, FR-NOTIF-010, PAGES.md §3.72.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@bbpos/ui';
import type { NotificationType } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { notificationHref } from '@/pages/notifications/notification-links';
import { ROLE_LEVELS } from '@/constants/product';
import {
  connectSocket,
  disconnectSocket,
  onSocketEvent,
  subscribeSocketStatus,
  type SocketStatus,
} from '@/lib/socket-client';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Sparse payload emitted by the gateway. Mirrors the backend
 * `emitNotification` call site.
 */
interface IncomingNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  created_at: string;
  user_id?: string | null;
  location_id?: string | null;
  is_read?: boolean;
  data?: Record<string, unknown> | null;
}

// ─── Toast routing ───────────────────────────────────────────────────────────

const TOAST_WORTHY: ReadonlySet<NotificationType> = new Set([
  'SYNC_CONFLICT',
  'LOW_STOCK',
  'CREDIT_LIMIT',
  'EXPENSE_APPROVAL',
  'APPROVAL_REQUEST',
  'APPROVAL_RESULT',
  'PLATFORM_SUSPENSION',
  'TENANT_CANCELLED',
  'BILLING_FAILED',
  // Outbound mail is dead until the OWNER re-enters credentials — the silent
  // counter bump is not enough (email-smtp plan §6).
  'EMAIL_DELIVERY_FAILED',
  // T11 — a customer answered a quote on the public link.
  'QUOTE_APPROVED',
  'QUOTE_REJECTED',
  'QUOTE_CHANGES_REQUESTED',
]);

const DANGER_TYPES: ReadonlySet<NotificationType> = new Set([
  'SYNC_CONFLICT',
  'CREDIT_LIMIT',
  'PLATFORM_SUSPENSION',
  'TENANT_CANCELLED',
  'BILLING_FAILED',
  'EMAIL_DELIVERY_FAILED',
]);

const WARNING_TYPES: ReadonlySet<NotificationType> = new Set([
  'LOW_STOCK',
  'EXPENSE_APPROVAL',
  'APPROVAL_REQUEST',
]);

function toastVariant(type: string): 'success' | 'warning' | 'error' | 'info' {
  if (DANGER_TYPES.has(type as NotificationType)) return 'error';
  if (WARNING_TYPES.has(type as NotificationType)) return 'warning';
  if (type === 'APPROVAL_RESULT' || type === 'QUOTE_APPROVED') return 'success';
  if (type === 'QUOTE_REJECTED') return 'error';
  return 'info';
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useRealtimeNotifications(): void {
  const profile = useAuthStore((s) => s.profile);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Refs so listeners don't rebind every render.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const qcRef = useRef(queryClient);
  qcRef.current = queryClient;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const roleLevelRef = useRef(0);
  roleLevelRef.current = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const hasConnectedOnceRef = useRef(false);

  useEffect(() => {
    // Admin users without a tenant context (platform-only viewers) still
    // get a socket, but only for cross-tenant events like the sync health
    // overview. A notification listener without a tenant still listens on
    // the current socket — the backend gateway rejects connections without
    // a valid tenant JWT anyway, so this is a no-op in that case.
    const tenantId = profile?.tenant?.id;
    if (!tenantId) return;

    connectSocket();

    const unsubscribeStatus = subscribeSocketStatus((next: SocketStatus) => {
      if (next === 'connected') {
        if (hasConnectedOnceRef.current) {
          // Reconnect after drop — refresh list + count to cover any
          // events missed while the socket was down.
          void qcRef.current.invalidateQueries({ queryKey: ['notifications'] });
        }
        hasConnectedOnceRef.current = true;
      }
    });

    const unsubscribeNew = onSocketEvent<IncomingNotification>('notification:new', (payload) => {
      // Defensive: drop malformed events.
      if (!payload || typeof payload.id !== 'string' || !payload.type) return;

      // Invalidate the cached list + unread-count so the visible page
      // refetches through its own React Query hooks. Admin doesn't push
      // into a Zustand store; REST is the source of truth.
      void qcRef.current.invalidateQueries({ queryKey: ['notifications'] });

      if (!TOAST_WORTHY.has(payload.type as NotificationType)) return;

      // T24: 'Open' goes to the linked record, else the notification's detail view.
      const path = notificationHref(
        { id: payload.id, action_url: null, metadata: payload.data },
        roleLevelRef.current,
      );
      toastRef.current({
        variant: toastVariant(payload.type),
        title: payload.title,
        description: payload.message,
        action: { label: 'Open', onClick: () => navigateRef.current(path) },
      });
    });

    return () => {
      unsubscribeNew();
      unsubscribeStatus();
      disconnectSocket();
      hasConnectedOnceRef.current = false;
    };
  }, [profile?.tenant?.id]);
}
